import {
  AffiliateUrlIcon,
  getCookie,
  ProsConsItem,
} from '@kizi/comparator-components';
import { ComparatorContext, KiziLogger, useField } from '@kizi/comparator-core';
import { Button, Div, Heading, P } from '@kizi/core-components';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { IconV2 as Icon } from '@kizi/shared-components';

const navigateToAffiliate = (orderId: string, url: string) => {
  if (orderId) {
    location.href = url;
  }
};

interface AffiliateUrlModalWrapperProps {
  affiliateUrlClientId: string;
}

export const AffiliateUrlModalWrapper: React.FC<AffiliateUrlModalWrapperProps> =
  React.memo(({ affiliateUrlClientId }) => {
    const selectedProductField = useField('overview.selectedProduct');

    const { api } = useContext(ComparatorContext);
    const [orderIdResult, setOrderIdResult] = useState<any | null>({});

    const searchParams = new URLSearchParams(location.search);
    let commonTechnical = [
      {
        transactionReference: 'none',
      },
    ];
    let productCode = searchParams.get('productCode') || '';
    let affiliateUrl = searchParams.get('affiliateUrl') || '';
    let product;
    let hasCashbackIncentive = false;
    if (selectedProductField?.value) {
      product = JSON.parse(selectedProductField?.value as any);
      commonTechnical = product.commonTechnical;
      productCode = product.productCode;
      affiliateUrl = product.affiliateUrl;

      hasCashbackIncentive =
        product?.incentive?.productIncentiveType === 'CASHBACK';
      console.log({ product, hasCashbackIncentive });
    }

    useEffect(() => {
      (async () => {
        const result = await api.orderAffiliateContentPackage(
          commonTechnical[0].transactionReference,
          productCode,
          { userDesiresIncentive: false },
          'os-triple-play',
        );
        setOrderIdResult(result);
      })();
    }, []);

    let affiliateProperUrl = affiliateUrl;
    try {
      affiliateProperUrl = decodeURI(affiliateUrl);
    } catch (error) {
      //do nothing
    }

    affiliateProperUrl = affiliateProperUrl
      .replace('%clientid%', affiliateUrlClientId)
      .replace('%reference%', getCookie('ref') ?? '');

    if (
      Array.isArray(commonTechnical) &&
      orderIdResult.orderId &&
      commonTechnical.length > 0
    ) {
      affiliateProperUrl = affiliateProperUrl
        .replace('%referenceid%', commonTechnical[0].transactionReference)
        .replace('%orderid%', orderIdResult.orderId);
    }

    useEffect(() => {
      if (
        Array.isArray(commonTechnical) &&
        orderIdResult.orderId &&
        commonTechnical.length > 0 &&
        !hasCashbackIncentive
      ) {
        navigateToAffiliate(orderIdResult.orderId, affiliateProperUrl);
      } else {
        KiziLogger.logError({
          message: 'Could not open affiliate URL',
          eventId: 0,
          eventName: 'Unhandled error',
          member: api.credentials.clientId as string,
          data: {
            orderIdResult,
            commonTechnical,
            ...(selectedProductField?.value && {
              selectedProduct: JSON.parse(
                selectedProductField?.value as string,
              ),
            }),
          },
        });
      }
    }, [orderIdResult.orderId]);

    const onClick = useCallback(() => {
      navigateToAffiliate(orderIdResult.orderId, affiliateProperUrl);
    }, [orderIdResult.orderId]);

    if (!selectedProductField) return null;

    return (
      <OsTriplePlayAffiliateUrlModal
        onClick={onClick}
        hasCashbackIncentive={hasCashbackIncentive}
        data={product}
      />
    );
  });

const CashBackContent = ({ onClick, data }) => {
  return (
    <Div variant="affiliateUrlPage">
      <Div minWidth={['unset', 'unset', '200px', '200px']} width={['100%']}>
        <Div
          css={{
            borderTopLeftRadius: '24px',
            borderTopRightRadius: '24px',
            padding: '12px 32px',
            display: 'flex',
            gap: 'var(--8pt-Grid-4px, 4px)',
            bg: '#E8F2FF',
          }}
        >
          <Icon name={'piggy-bank'} size={33} fill="#105ECB" />
          <P
            variant="footnoteImportant"
            css={{
              color: '#105ECB',
              fontWeight: '700',
              fontSize: '24px',
            }}
          >
            €{data?.incentive?.incentiveAmount} Cashback - via Overstappen.nl
          </P>
        </Div>
        <Div
          variant="affiliateUrlPageModal"
          css={{
            borderTopLeftRadius: '0px',
            borderTopRightRadius: '0px',
            margin: '0px !important',
          }}
        >
          <Heading as="h4" variant="H4" my={0} mb={1}>
            Voorwaarden van de cashback:
          </Heading>
          <ProsConsItem
            text={`Accepteer de cookies bij de ${data.provider.name}`}
            type="pros"
          />
          <ProsConsItem
            text={`Vul het formulier in op de cashback pagina`}
            type="pros"
          />
          <ProsConsItem
            text={`Ontvang jouw cashback binnen een aantal weken, conform de voorwaarden`}
            type="pros"
          />

          <Div
            css={{
              marginTop: '20px',
              display: 'flex',
              justifyContent: 'right',
            }}
          >
            <Button onClick={onClick} variant="primary">
              Ga door naar {data.provider.name}
            </Button>
          </Div>
        </Div>
      </Div>
    </Div>
  );
};

const OsTriplePlayAffiliateUrlModal = ({
  onClick,
  hasCashbackIncentive,
  data,
}: {
  onClick: () => void;
  hasCashbackIncentive: boolean;
  data: any;
}): JSX.Element => {
  if (hasCashbackIncentive) {
    return <CashBackContent onClick={onClick} data={data} />;
  }
  return (
    <Div variant="affiliateUrlPage">
      <Div
        variant="affiliateUrlPageModal"
        css={{ display: 'flex', alignItems: 'center' }}
      >
        <AffiliateUrlIcon />
        <Heading as="h2" variant="H2" textAlign="center" my={0}>
          {'Binnen enkele seconden sturen we je door naar de aanbieder'}
        </Heading>
        <P variant="bodyRegular" color="second.0" textAlign="center">
          {'Verschijnt deze pagina niet binnen enkele seconden, '}
          <Button variant="transparent" onClick={onClick}>
            {'klik dan hier'}
          </Button>
        </P>
      </Div>
    </Div>
  );
};
